import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Play } from 'lucide-react';
import {
  SPORTS_FUTURE_PLAYLIST_ID,
  fetchPlaylistVideos,
  youtubeEmbedPlaylistUrl,
  youtubePlaylistUrl,
  type PlaylistVideo,
} from '../utils/youtubePlaylist';

const PLAYLIST_ID = SPORTS_FUTURE_PLAYLIST_ID;

const YouTubePlaylistFeed = () => {
  const [videos, setVideos] = useState<PlaylistVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const items = await fetchPlaylistVideos(PLAYLIST_ID, 12);
        setVideos(
          items.map((video) => ({
            ...video,
            publishedAt: video.publishedAt
              ? new Date(video.publishedAt).toLocaleDateString()
              : '',
          }))
        );
        setError(null);
      } catch (err) {
        console.error('Error fetching YouTube playlist:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);
  
  const openVideoModal = (videoId: string) => {
    setSelectedVideo(videoId);
    setModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };
  
  const closeVideoModal = () => {
    setModalOpen(false);
    setSelectedVideo(null);
    // Restore body scrolling
    document.body.style.overflow = 'auto';
  };
  
  const VideoModal = () => {
    if (!modalOpen || !selectedVideo) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
        onClick={closeVideoModal}
      >
        <div
          className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={closeVideoModal}
            className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-colors"
            aria-label="Close video"
          >
            <X size={24} />
          </button>
          
          <div className="relative pb-[56.25%] h-0">
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full"
            ></iframe>
          </div>
        </div>
      </motion.div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error || videos.length === 0) {
    return (
      <div className="mt-12 space-y-6">
        <h2 className="text-3xl font-bold mb-4 text-center">Latest Videos</h2>
        <div className="aspect-video w-full overflow-hidden rounded-lg shadow-md">
          <iframe
            src={youtubeEmbedPlaylistUrl(PLAYLIST_ID)}
            title="SportsFuture playlist"
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="text-center">
          <a
            href={youtubePlaylistUrl(PLAYLIST_ID)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-primary hover:bg-primary-600 text-white font-medium rounded-md px-6 py-2 transition-colors"
          >
            View All Videos
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mt-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-3xl font-bold mb-8 text-center">Latest Videos</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <motion.div
              key={video.id}
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer"
              onClick={() => openVideoModal(video.id)}
            >
              <div className="relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full aspect-video object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 opacity-0 hover:opacity-100 transition-opacity">
                  <div className="bg-primary text-white rounded-full p-3">
                    <Play size={24} />
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 line-clamp-2">{video.title}</h3>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{video.channelTitle}</span>
                  <span>{video.publishedAt}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <a
            href={youtubePlaylistUrl(PLAYLIST_ID)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-primary hover:bg-primary-600 text-white font-medium rounded-md px-6 py-2 transition-colors"
          >
            View All Videos
          </a>
        </div>
      </motion.div>
      
      <VideoModal />
    </div>
  );
};

export default YouTubePlaylistFeed;